import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./FirstlightPaywall.module.scss"

/**
 * FirstlightPaywall (`/firstlight`) — a self-contained ONBOARDING paywall for
 * Private Photo Vault, staged as a small light "activation console": the face of
 * your brand-new private vault, waiting to be brought online. The whole screen is
 * a warm, flat control panel — a photo window holding your first memories, a big
 * POWER control, and a rail of indicator lights — not a centered hero card.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/firstlight.md
 *
 * Mechanic (new to the ledger): BRING IT ONLINE / a device boot sequence. One tap
 * on the power control runs a short, satisfying "self-test": your first photos
 * wake from standby, a warm sweep of first light passes over them, and four
 * indicator rows light up one-by-one — Backup · Sync · Full-quality · Private —
 * as the status flips STANDBY → ONLINE. The console teaches itself once on load,
 * then hands the switch to you. Purchase is never gated on the demo; the power
 * control only illustrates what starting your plan turns on.
 *
 * Onboarding framing (honest, behavioural): FUTURE-SELF + COMMITMENT + FRESH-START.
 * "Set it up once today, then it just runs" — the pitch is the calm of a brand-new
 * user who never has to think about it again, reached through the single deliberate
 * act of switching it on. The register is confident / reassuring / a little
 * satisfying — not the cosmic-awe (Aurora), deep-calm descent (Abyss), steel-crank
 * (Vault Door), future-scrub tower (Longview), resin-fill gem (Amber) or
 * before/after seam (Sightline) of the six prior onboarding paywalls.
 *
 * Every ledger axis is chosen to differ:
 *   - Layout:      a LIGHT appliance FRONT PANEL — status strip + photo window +
 *                  round power control + a vertical indicator rail — with the plan
 *                  module grounded separately below. Not a hero card / canvas /
 *                  gem / time-tower / before-after split.
 *   - Interaction: POWER-ON boot sequence (one decisive tap kicks a sequenced
 *                  self-test), distinct from every prior scrub / hold / drag / flip /
 *                  toss / dial / riffle / stitch / lasso / lever / wind / raise-fill.
 *   - Register:    confident / reassuring / quietly satisfying "it's handled."
 *   - Art:         LIGHT warm porcelain panel; brand blue = the ONLINE / protected
 *                  state (lit LEDs, rings, featured yearly, CTA); a single warm
 *                  amber "first light" sweep. Breaks the dark/skeuomorphic cluster.
 *   - Value:       PEACE-OF-MIND via set-it-and-forget backup + sync + full-quality
 *                  originals, private — everything running for a brand-new user.
 *
 * Honest & App Store compliant: turning it on starts real Premium features only
 * (automatic backup, sync on every device, full-resolution originals, private &
 * encrypted). Copy never implies data is lost if the plan ends — it says the
 * opposite. Yearly is the featured, default plan and carries the most prominent
 * price; monthly is small and secondary. Save % is exact yearly-vs-monthly
 * arithmetic; there is no countdown or fake scarcity. Photos are bundled demo
 * images from `public/byheart/` (no network egress).
 */

type PlanKey = "yearly" | "monthly"
type Phase = "standby" | "booting" | "online"

// your first memories, sitting in the vault window (bundled demo photos)
const PHOTOS = [
  { src: "m02.jpg", rot: -8, dx: 0, dy: 6, z: 1 },
  { src: "m05.jpg", rot: 5, dx: 0, dy: 2, z: 2 },
  { src: "m01.jpg", rot: -3, dx: 0, dy: 0, z: 4 },
  { src: "m07.jpg", rot: 7, dx: 0, dy: 3, z: 3 },
  { src: "m03.jpg", rot: -6, dx: 0, dy: 8, z: 1 },
].map((p) => ({ ...p, src: `/byheart/${p.src}` }))

// the indicator rail — the real things that come online with Premium
const RAIL = [
  {
    key: "backup",
    label: "Automatic backup",
    sub: "Safe even if your phone is lost or breaks",
    icon: "cloud",
  },
  {
    key: "sync",
    label: "On every device",
    sub: "Your vault, in sync everywhere you sign in",
    icon: "sync",
  },
  {
    key: "quality",
    label: "Full-quality originals",
    sub: "Kept exactly as you shot them — never shrunk",
    icon: "image",
  },
  {
    key: "private",
    label: "Private & encrypted",
    sub: "Only your passcode ever opens it",
    icon: "lock",
  },
] as const

// ── tiny inline icons ─────────────────────────────────────────────────────────
const Power = ({ size = 30 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 3v9"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
    />
    <path
      d="M6.6 6.4a7.5 7.5 0 1 0 10.8 0"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
    />
  </svg>
)

const Tick = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const RailIcon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      {name === "cloud" && (
        <path
          d="M7 18a4 4 0 0 1-.4-8 5.5 5.5 0 0 1 10.6 1.2A3.4 3.4 0 0 1 17 18H7Z"
          {...common}
        />
      )}
      {name === "sync" && (
        <>
          <path d="M4 12a8 8 0 0 1 13.7-5.6L20 8" {...common} />
          <path d="M20 12a8 8 0 0 1-13.7 5.6L4 16" {...common} />
          <path d="M20 4v4h-4M4 20v-4h4" {...common} />
        </>
      )}
      {name === "image" && (
        <>
          <rect x="3.5" y="4.5" width="17" height="15" rx="2.4" {...common} />
          <circle cx="9" cy="10" r="1.6" {...common} />
          <path d="M5 17l4.5-4.2L13 16l3-2.6L19 17" {...common} />
        </>
      )}
      {name === "lock" && (
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" {...common} />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" {...common} />
        </>
      )}
    </svg>
  )
}

const clearAll = (arr: number[]) => {
  arr.forEach((t) => window.clearTimeout(t))
  arr.length = 0
}

const FirstlightPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const yearlyTrial = yearlyProduct?.hasTrial
    ? (yearlyProduct?.introPricing?.period ?? 7)
    : 0
  const monthlyTrial = monthlyProduct?.hasTrial
    ? (monthlyProduct?.introPricing?.period ?? 3)
    : 0

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.max(0, Math.round((1 - y / (m * 12)) * 100))
  }, [yearlyProduct, monthlyProduct])

  // ── console state ───────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>("standby")
  const [lit, setLit] = useState(0) // how many rail rows are lit
  const [sweep, setSweep] = useState(0) // bump to retrigger the light sweep
  const [showClose, setShowClose] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")

  const timers = useRef<number[]>([])

  const powerOn = useCallback(() => {
    clearAll(timers.current)
    setSweep((s) => s + 1)
    setPhase("booting")
    setLit(0)
    const step = 300
    RAIL.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(() => setLit(i + 1), 220 + i * step),
      )
    })
    timers.current.push(
      window.setTimeout(() => setPhase("online"), 220 + RAIL.length * step),
    )
  }, [])

  const powerOff = useCallback(() => {
    clearAll(timers.current)
    setPhase("standby")
    setLit(0)
  }, [])

  const toggle = () => {
    native.logMixpanelEvent("html_firstlight_power", {
      to: phase === "online" ? "standby" : "online",
    })
    phase === "online" ? powerOff() : powerOn()
  }

  // teaching boot: run the self-test once on load, then hand over the switch
  useEffect(() => {
    if (reduceMotion) {
      setPhase("online")
      setLit(RAIL.length)
      setShowClose(true)
      return
    }
    const boot = window.setTimeout(() => powerOn(), 640)
    const close = window.setTimeout(() => setShowClose(true), 1400)
    return () => {
      window.clearTimeout(boot)
      window.clearTimeout(close)
      clearAll(timers.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // match the safe-area strips to the porcelain panel
  useEffect(() => {
    actions.setHeaderInsetColor("#eef2fb")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  // ── plan + purchase wiring ──────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_firstlight_purchase", {
      plan,
      phase,
    })
    if (product) native.subscribe(product)
  }

  const online = phase === "online"
  const statusLabel =
    phase === "standby"
      ? "STANDBY"
      : phase === "booting"
        ? "STARTING…"
        : "ONLINE"

  const trialDays = plan === "yearly" ? yearlyTrial : monthlyTrial
  const ctaSub =
    plan === "yearly"
      ? trialDays > 0
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : trialDays > 0
        ? `${trialDays} days free, then ${monthlyPrice}/mo · cancel anytime`
        : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <button
        className={`${styles.close} ${showClose ? styles.closeShown : ""}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <span className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width={13} height={13}>
              <path
                d="M12 2.6l7 2.7v5.2c0 4.6-3 8.2-7 10.9-4-2.7-7-6.3-7-10.9V5.3l7-2.7Z"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Private Photo Vault
        </span>
        <h1 className={styles.title}>
          Your vault is ready.
          <br />
          Bring it online.
        </h1>
        <p className={styles.sub}>
          Switch it on once today — then backup, sync and full-quality keep
          running on their own. Nothing to think about again.
        </p>
      </header>

      {/* ── the activation console ─────────────────────────────────────────────── */}
      <section
        className={`${styles.console} ${online ? styles.consoleOn : ""} ${
          phase === "booting" ? styles.consoleBoot : ""
        }`}
        aria-label="Vault activation console"
      >
        <div className={styles.consoleTop}>
          <span className={styles.plate}>VAULT · 01</span>
          <span
            className={`${styles.led} ${online ? styles.ledOn : ""} ${
              phase === "booting" ? styles.ledBoot : ""
            }`}
            aria-live="polite"
          >
            <span className={styles.ledDot} />
            {statusLabel}
          </span>
        </div>

        {/* photo window — your first memories, waking up */}
        <div className={styles.window} aria-hidden>
          <div className={styles.photos}>
            {PHOTOS.map((p, i) => (
              <span
                key={p.src}
                className={`${styles.photo} ${
                  phase !== "standby" ? styles.photoAwake : ""
                }`}
                style={{
                  zIndex: p.z,
                  transitionDelay: phase !== "standby" ? `${i * 90}ms` : "0ms",
                  transform: `translate(${p.dx}px, ${p.dy}px) rotate(${p.rot}deg)`,
                }}
              >
                <img src={p.src} alt="" draggable={false} />
                <span className={styles.ring} />
              </span>
            ))}
          </div>
          <span key={sweep} className={styles.sweep} />
          <span className={styles.count}>
            {online ? "Protected from today" : "Waiting to start"}
          </span>
        </div>

        {/* the power control */}
        <div className={styles.control}>
          <button
            className={`${styles.power} ${online ? styles.powerOn : ""} ${
              phase === "booting" ? styles.powerBoot : ""
            }`}
            onClick={toggle}
            aria-pressed={online}
            aria-label={online ? "Vault online" : "Bring vault online"}
          >
            <span className={styles.powerHalo} aria-hidden />
            <Power size={30} />
          </button>
          <span className={styles.controlHint}>
            {phase === "standby"
              ? "Tap to bring it online"
              : phase === "booting"
                ? "Starting your vault…"
                : "It’s on — tap to see it again"}
          </span>
        </div>

        {/* indicator rail — real features lighting up in sequence */}
        <ul className={styles.rail}>
          {RAIL.map((r, i) => {
            const isLit = i < lit
            return (
              <li
                key={r.key}
                className={`${styles.rrow} ${isLit ? styles.rowLit : ""}`}
              >
                <span className={styles.rowIcon}>
                  <RailIcon name={r.icon} />
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowLabel}>{r.label}</span>
                  <span className={styles.rowSub}>{r.sub}</span>
                </span>
                <span className={styles.rowLed} aria-hidden>
                  <Tick size={11} />
                </span>
              </li>
            )
          })}
        </ul>
      </section>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
          <span className={styles.planMain}>
            <span className={styles.radio} aria-hidden />
            <span className={styles.planNameWrap}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.planNote}>
                {yearlyTrial > 0
                  ? `${yearlyTrial}-day free trial included`
                  : "Everything, all year"}
              </span>
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>{yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.selMonth : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>
          {plan === "yearly" ? "Bring my vault online" : "Start monthly"}
        </span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours. Nothing is ever deleted, and if your plan
        ever ends you keep every photo already in your vault.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default FirstlightPaywall
